/** downscaleImage — client-side resize + JPEG re-encode utility.
 * Re-encodes when EITHER (a) width exceeds maxWidth, (b) height exceeds maxHeight,
 * (c) non-JPEG file larger than sizeThresholdBytes, or (d) JPEG larger than 2× threshold.
 * Otherwise returns the original file untouched. */
export async function downscaleImage(
  file,
  { maxWidth = 1600, maxHeight = 2200, quality = 0.85, sizeThresholdBytes = 500_000 } = {}
) {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((res, rej) => {
      const el = new Image();
      el.onload = () => res(el);
      el.onerror = rej;
      el.src = url;
    });
    const overWide = img.naturalWidth > maxWidth;
    const overTall = img.naturalHeight > maxHeight;
    const isJpeg = (file.type || "").toLowerCase() === "image/jpeg";
    const oversized = isJpeg
      ? file.size > 2 * sizeThresholdBytes
      : file.size > sizeThresholdBytes;
    if (!overWide && !overTall && !oversized) return file;

    const scale = Math.min(
      overWide ? maxWidth / img.naturalWidth : 1,
      overTall ? maxHeight / img.naturalHeight : 1,
      1,
    );
    const w = Math.round(img.naturalWidth * scale);
    const h = Math.round(img.naturalHeight * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    canvas.getContext("2d").drawImage(img, 0, 0, w, h);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    if (!blob) return file;
    // Guard: JPEG round-trip inflated a small PNG → keep the original.
    if (blob.size >= file.size && !overWide && !overTall) return file;
    return new File(
      [blob],
      (file.name || "cover").replace(/\.[^.]+$/, "") + ".jpg",
      { type: "image/jpeg" },
    );
  } finally {
    URL.revokeObjectURL(url);
  }
}
